import { useTranslation } from "react-i18next";
import { SearchHistoryDeleteButton } from "./SearchHistoryDeleteButton";
import { ISearchHistoryInfoType } from "../types/search_history_type";
import { useNavigate } from "react-router-dom";
import { PAGE_NAME_FEEDBACK } from "../../../../constants/common_constants";
import { useApi, useErrorDialog } from "../../../../hooks";
import { IInferenceFactorItemListType } from "../../select_phenomenon/types/select_phenomenon_type";
import { useDtsAppContext } from "../../../../components/provider/DtsAppContextProvider";
import { SetStateAction, useEffect, useState } from "react";
import {
  getPhenomenon,
  getSearchHistoryDetail,
} from "../../../api/dts_api_wrapper";
import { FaMinus, FaPlus } from "react-icons/fa6";
import React from "react";

interface SearchHistoryListProps {
  searchHistoryInfo: ISearchHistoryInfoType;
  showConfirmDialog: (message: string) => void;
  userId: string;
  setSearchHistoryId: React.Dispatch<SetStateAction<string>>;
  isLastList: boolean;
}

export const SearchHistoryList: React.FC<SearchHistoryListProps> = ({
  searchHistoryInfo,
  showConfirmDialog,
  userId,
  setSearchHistoryId,
  isLastList,
}) => {
  console.log("searchHistoryInfo =", searchHistoryInfo);
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  const { handleApi } = useApi();

  const {
    setDacInferenceFactor,
    setDacSearchHistoryId,
    setDacSubjectName,
    setDacModel,
    setDacOccurrenceTiming,
    setDacPhenomenon,
    setDacMachineNumber,
  } = useDtsAppContext();

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  // 検索履歴項目のアコーディオン表示
  const [isOpen, setIsOpen] = useState(false);

  const [phenomenonDisplayList, setPhenomenonDisplayList] = useState<
    JSX.Element[]
  >([]);

  // 発生タイミングの分岐
  const getOccurrenceTimingLabel = (value: string) => {
    switch (value) {
      case "1":
        return t("検索履歴画面.キーオン");
      case "2":
        return t("検索履歴画面.クランキング中");
      case "3":
        return t("検索履歴画面.運転中");
      default:
        return "";
    }
  };

  // 検索履歴に保存された現象一覧を表示用データに変換する関数
  const getPhenomenonDisplayList = async () => {
    console.log("userId", userId);
    console.log("occurrenceTiming", searchHistoryInfo.occurrenceTiming);
    console.log("language", searchHistoryInfo.language);
    try {
      const phenomenonMaster = await handleApi(
        getPhenomenon,
        userId,
        searchHistoryInfo.occurrenceTiming,
        searchHistoryInfo.language,
      );
      console.log("phenomenonMaster", phenomenonMaster);
      if ("errorMessage" in phenomenonMaster) {
        console.error(phenomenonMaster.errorMessage);
        throw new Error("failed to get phenomenon");
      }

      const normalize = (text: string) => text.replace(/"/g, "").trim();

      const selected = searchHistoryInfo.phenomenons
        .split(",")
        .map((p) => normalize(p));

      // 現象マスタが空の場合は、検索履歴に保存されている現象をそのまま表示する
      if (!phenomenonMaster.phenomenon.phenomenons.length) {
        setPhenomenonDisplayList(
          selected.map((item, index) => <div key={index}>{item}</div>),
        );
        return;
      }

      const result: string[] = [];

      // 現象文言の変更時に判定で使用
      const displayed: string[] = [];

      for (const item of phenomenonMaster.phenomenon.phenomenons) {
        const parent = item.question1;

        const selectedChildren = parent.question2.filter((child: any) =>
          selected.some(
            (s) =>
              normalize(child.label).includes(s) ||
              s.includes(normalize(child.label)),
          ),
        );

        // 親現象(第1階層)と子現象(第2階層)を表示対象として追加する
        if (selectedChildren.length > 0) {
          result.push(parent.label);
          displayed.push(normalize(parent.label));

          selectedChildren.forEach((child: any) => {
            result.push(`- ${child.label}`);
            displayed.push(normalize(child.label));
          });
        } else if (selected.includes(normalize(parent.label))) {
          result.push(parent.label);
          displayed.push(normalize(parent.label));
        }
      }

      // 一致しなかった現象は保存されていた第2階層現象をそのまま表示する
      selected.forEach((item) => {
        const matched = displayed.some(
          (d) => d.includes(item) || item.includes(d),
        );
        if (!matched) {
          result.push(`${item}`);
        }
      });

      setPhenomenonDisplayList(
        result.map((item, index) => (
          <div key={index} className={item.startsWith("-") ? "ml-2" : ""}>
            {item}
          </div>
        )),
      );
    } catch (err) {
      console.log("[Search History]handleApi err :", err);
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      ); //エラー", "通信エラーが発生しました。
    }
  };

  useEffect(() => {
    if (isOpen) {
      getPhenomenonDisplayList();
    }
  }, [isOpen]);

  //検索日時の表示をフォーマット
  const formattedDateTime = `${searchHistoryInfo.searchDateTime.slice(
    0,
    4,
  )}/${searchHistoryInfo.searchDateTime.slice(
    4,
    6,
  )}/${searchHistoryInfo.searchDateTime.slice(
    6,
    8,
  )} ${searchHistoryInfo.searchDateTime.slice(
    8,
    10,
  )}:${searchHistoryInfo.searchDateTime.slice(
    10,
    12,
  )}:${searchHistoryInfo.searchDateTime.slice(12, 14)}`;

  const handleOnSearchHistoryList = async () => {
    //何らかの理由でuserId または searchHistoryIdが読み込めなかった
    if (!userId || !searchHistoryInfo.searchHistoryId) {
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      ); //エラー", "通信エラーが発生しました。(トップページに遷移)
      return; // パラメータ不足の場合は処理を終了
    }
    try {
      console.log("handleOnSearchHistoryList start");
      //検索履歴情報詳細の取得
      const searchHistoryDetailResult = await handleApi(
        getSearchHistoryDetail,
        userId,
        searchHistoryInfo.searchHistoryId,
      );
      //検索履歴情報詳細取得エラーの場合
      if ("errorMessage" in searchHistoryDetailResult) {
        console.log("errorMessage: ", searchHistoryDetailResult.errorMessage);
        throw new Error("failed to get searchHistory detail info");
      }

      //ソートした検索履歴情報詳細をContext+ローカルストレージに保存
      sortSearchHistiryDetail(searchHistoryDetailResult);
      const searchHistoryDetailStr = JSON.stringify(searchHistoryDetailResult);
      setDacInferenceFactor(searchHistoryDetailStr);
      localStorage.setItem("inferenceFactor", searchHistoryDetailStr);

      //検索履歴情報ID, 案件名, 型式, 機番をContext+ローカルストレージに保存
      setDacSearchHistoryId(searchHistoryInfo.searchHistoryId);
      localStorage.setItem(
        "searchHistoryId",
        searchHistoryInfo.searchHistoryId,
      );
      setDacSubjectName(searchHistoryInfo.subjectName);
      localStorage.setItem("subjectName", searchHistoryInfo.subjectName);
      setDacModel(searchHistoryInfo.model);
      localStorage.setItem("model", searchHistoryInfo.model);
      setDacOccurrenceTiming(Number(searchHistoryInfo.occurrenceTiming));
      localStorage.setItem(
        "occurrenceTiming",
        searchHistoryInfo.occurrenceTiming,
      );
      setDacPhenomenon(searchHistoryInfo.phenomenons);
      localStorage.setItem("phenomenon", searchHistoryInfo.phenomenons);
      if (searchHistoryInfo.machineNumber) {
        setDacMachineNumber(searchHistoryInfo.machineNumber);
        localStorage.setItem("machineNumber", searchHistoryInfo.machineNumber);
      } else {
        // 機番未入力の場合、Contextを""に更新+ローカルストレージリセット
        setDacMachineNumber("");
        localStorage.removeItem("machineNumber");
      }

      navigate(`/${PAGE_NAME_FEEDBACK}`);
    } catch (err) {
      console.log("[Search History]handleApi err :", err);
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
      ); //エラー", "通信エラーが発生しました。
    }
  };

  return (
    //取得した検索履歴一覧の最後尾の履歴情報の場合, 下の余白は付けない
    <div
      className={`relative w-full bg-white border border-gray-500 ${
        !isLastList && "mb-4"
      }`}
    >
      {
        searchHistoryInfo.status === "1" && (
          // 回答状況(status)が完了(1)の場合にボタン表示
          <SearchHistoryDeleteButton
            showConfirmDialog={showConfirmDialog}
            searchHistoryId={searchHistoryInfo.searchHistoryId}
            setSearchHistoryId={setSearchHistoryId}
          />
        ) // 未完了(0)の場合何も表示しない
      }
      <div className="pl-5 pr-5 py-3" onClick={handleOnSearchHistoryList}>
        <p className="pb-1">
          {t("検索履歴画面.検索日時")} :{/* 検索日時 */}
          <span className="font-normal pl-1">{formattedDateTime}</span>
        </p>
        <p className="">
          {t("検索履歴画面.案件名")} :{/* 案件名 */}
          <span className="font-normal pl-1 pb-1">
            {searchHistoryInfo.subjectName}
          </span>
        </p>
        <p className="pb-1">
          {t("検索履歴画面.型式")} :{/* 型式 */}
          <span className="font-normal pl-1">{searchHistoryInfo.model}</span>
        </p>
        {/* アコーディオンボタン */}
        <div
          className="absolute right-1.5 overflow-visible bottom-2 text-[#535353]"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {isOpen ? <FaMinus /> : <FaPlus />}
        </div>
        {isOpen && (
          <>
            <p className="pb-1">
              {t("検索履歴画面.発生タイミング")} :{/* 発生タイミング */}
              <span className="font-normal pl-1">
                {getOccurrenceTimingLabel(searchHistoryInfo.occurrenceTiming)}
              </span>
            </p>
            <p>{t("検索履歴画面.現象")} :</p>
            <div className="font-normal pl-5 pb-1">{phenomenonDisplayList}</div>
          </>
        )}
        {!!searchHistoryInfo.machineNumber && (
          <p className="pb-1">
            {t("検索履歴画面.機番")} :{/* 機番 */}
            <span className="font-normal pl-1">
              {searchHistoryInfo.machineNumber}
            </span>
          </p>
        )}
        <p>
          {t("検索履歴画面.回答状況")} :{/* 回答状況 */}
          <span className="font-normal pl-1">
            {searchHistoryInfo.status === "1"
              ? t("検索履歴画面.完了")
              : t("検索履歴画面.未完了")}
            {/* 完了/未完了 */}
          </span>
        </p>
      </div>
    </div>
  );
};

/* 取得した検索履歴情報詳細をpriorityに従ってソートする関数 */
const sortSearchHistiryDetail = (
  unsortedData: IInferenceFactorItemListType,
) => {
  unsortedData.inferenceFactor.sort((a, b) =>
    a.issueFactor.priority < b.issueFactor.priority ? -1 : 1,
  );
  unsortedData.inferenceFactor.map((value) => {
    return value.issueFactor.repairFactor.referenceInfo.sort((a, b) =>
      a.priority < b.priority ? -1 : 1,
    );
  });
  return unsortedData;
};
